import React, { useState, useEffect } from 'react';
import { AdminAuditLog } from '@curbside-quest/shared';

interface AuditLogEntry extends Omit<AdminAuditLog, 'timestamp'> {
  timestamp: Date;
  adminName?: string;
}

export const AuditLog: React.FC = () => {
  const [auditLogs, setAuditLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    adminId: '',
    action: '',
    dateFrom: '',
    dateTo: ''
  });

  useEffect(() => {
    loadAuditLogs();
  }, [filters]);

  const loadAuditLogs = async () => {
    try {
      setLoading(true);
      // This would call the Firebase function
      // const result = await functions.httpsCallable('getAdminAuditLog')(filters);
      // setAuditLogs(result.data.auditLogs);
      
      // Mock data for now
      const mockLogs: AuditLogEntry[] = [
        {
          id: '1',
          adminId: 'admin1',
          adminName: 'John Admin',
          action: 'approve_report',
          details: {
            reportId: 'report123',
            approvalNotes: 'Good quality report with clear images'
          },
          timestamp: new Date('2024-01-20T10:30:00'),
          ipAddress: '192.168.1.1'
        },
        {
          id: '2',
          adminId: 'admin1',
          adminName: 'John Admin',
          action: 'reject_report',
          details: {
            reportId: 'report124',
            rejectionReason: 'inappropriate_content',
            rejectionNotes: 'Contains inappropriate language'
          },
          timestamp: new Date('2024-01-20T09:15:00'),
          ipAddress: '192.168.1.1'
        },
        {
          id: '3',
          adminId: 'admin2',
          adminName: 'Jane Admin',
          action: 'ban_user',
          details: {
            targetUserId: 'user456',
            reason: 'spam_reports',
            duration: 7
          },
          timestamp: new Date('2024-01-19T16:45:00'),
          ipAddress: '192.168.1.2'
        },
        {
          id: '4',
          adminId: 'admin1',
          adminName: 'John Admin',
          action: 'set_user_claims',
          details: {
            targetUserId: 'user789',
            claims: { admin: true }
          },
          timestamp: new Date('2024-01-19T14:20:00'),
          ipAddress: '192.168.1.1'
        }
      ];

      setAuditLogs(mockLogs);
    } catch (error) {
      console.error('Error loading audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action: string) => {
    const icons: Record<string, string> = {
      approve_report: '✅',
      reject_report: '❌',
      ban_user: '🚫',
      unban_user: '✅',
      set_user_claims: '🔧',
      default: '📝'
    };
    return icons[action] || icons.default;
  };

  const getActionColor = (action: string) => {
    const colors: Record<string, string> = {
      approve_report: 'text-green-600 bg-green-50',
      reject_report: 'text-red-600 bg-red-50',
      ban_user: 'text-red-600 bg-red-50',
      unban_user: 'text-green-600 bg-green-50',
      set_user_claims: 'text-blue-600 bg-blue-50',
      default: 'text-gray-600 bg-gray-50'
    };
    return colors[action] || colors.default;
  };

  const formatActionName = (action: string) => {
    return action
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const formatDetails = (action: string, details: any) => {
    switch (action) {
      case 'approve_report':
        return `Approved report ${details.reportId}${details.approvalNotes ? ` - ${details.approvalNotes}` : ''}`;
      case 'reject_report':
        return `Rejected report ${details.reportId} (${details.rejectionReason})${details.rejectionNotes ? ` - ${details.rejectionNotes}` : ''}`;
      case 'ban_user':
        return `Banned user ${details.targetUserId} for ${details.reason}${details.duration ? ` (${details.duration} days)` : ' (permanent)'}`;
      case 'unban_user':
        return `Unbanned user ${details.targetUserId}${details.reason ? ` - ${details.reason}` : ''}`;
      case 'set_user_claims':
        return `Updated user claims for ${details.targetUserId}: ${JSON.stringify(details.claims)}`;
      default:
        return JSON.stringify(details);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">
          Admin Audit Log ({auditLogs.length})
        </h2>
        <button
          onClick={loadAuditLogs}
          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
        >
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h3 className="text-sm font-medium text-gray-900 mb-3">Filters</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Admin
            </label>
            <select
              value={filters.adminId}
              onChange={(e) => setFilters(prev => ({ ...prev, adminId: e.target.value }))}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="">All Admins</option>
              <option value="admin1">John Admin</option>
              <option value="admin2">Jane Admin</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Action
            </label>
            <select
              value={filters.action}
              onChange={(e) => setFilters(prev => ({ ...prev, action: e.target.value }))}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="">All Actions</option>
              <option value="approve_report">Approve Report</option>
              <option value="reject_report">Reject Report</option>
              <option value="ban_user">Ban User</option>
              <option value="unban_user">Unban User</option>
              <option value="set_user_claims">Set User Claims</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              From Date
            </label>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              To Date
            </label>
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
        </div>
      </div>

      {/* Audit Log Entries */}
      <div className="bg-white border rounded-lg overflow-hidden">
        {auditLogs.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 text-lg mb-2">📋</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No audit logs found</h3>
            <p className="text-gray-500">No admin actions match the current filters.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {auditLogs.map((log) => (
              <div key={log.id} className="p-6 hover:bg-gray-50">
                <div className="flex items-start space-x-4">
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm ${getActionColor(log.action)}`}>
                    {getActionIcon(log.action)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center space-x-2">
                        <span className="font-medium text-gray-900">
                          {formatActionName(log.action)}
                        </span>
                        <span className="text-sm text-gray-500">
                          by {log.adminName || log.adminId}
                        </span>
                      </div>
                      <time className="text-sm text-gray-500">
                        {log.timestamp.toLocaleString()}
                      </time>
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-2">
                      {formatDetails(log.action, log.details)}
                    </p>
                    
                    <div className="flex items-center space-x-4 text-xs text-gray-400">
                      <span>ID: {log.id}</span>
                      {log.ipAddress && <span>IP: {log.ipAddress}</span>}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Load More */}
      {auditLogs.length > 0 && (
        <div className="text-center">
          <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
            Load More Entries
          </button>
        </div>
      )}
    </div>
  );
};